/**
 * Data model for the company-census flow (lifted from the platforms-demo
 * existing-client activation POC and adapted for a real, deployable app):
 * login (or continue as guest) → business → team → dependents → confirmation.
 *
 * Two differences from the demo:
 *  - a guest can continue without logging in (and then supplies their own
 *    name + email on the business step);
 *  - dependents are collected manually only (the "ask my team" invite path
 *    was dropped).
 */

/** How the flow was entered. Drives pre-population and the guest fields. */
export type FlowMode = "guest" | "authenticated";

/**
 * What a completed HR-system pull brought in — reported on the review step.
 *
 * `excludedFormerCount` is the load-bearing one: a payroll roster is mostly
 * ex-employees (76 of 120 on the employer this was built for), and an employer
 * who knows their QuickBooks headcount needs the census to say why it shows
 * fewer people. Silence there reads as a broken import.
 */
export interface HrPullSummary {
  /** People the pull put on the roster. */
  importedCount: number;
  /** Former employees the pull deliberately left out. */
  excludedFormerCount: number;
  /** Census fields the provider withheld (BambooHR scope gaps). */
  fieldWarnings: string[];
}

/** Biological sex, as required for medical underwriting rating. */
export type Sex = "male" | "female";

/** The business profile we collect — name + EIN + mailing address + phone. */
export interface BusinessInfo {
  name: string;
  /** Employer Identification Number, `XX-XXXXXXX`. Required to advance. */
  ein: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
}

export const EMPTY_BUSINESS: BusinessInfo = {
  name: "",
  ein: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
};

/**
 * Submitter identity. Only collected (and required) in guest mode — for a
 * logged-in client we resolve it from the session server-side.
 */
export interface SubmitterInfo {
  name: string;
  email: string;
}

export const EMPTY_SUBMITTER: SubmitterInfo = { name: "", email: "" };

/** A single dependent on an employee's coverage. */
export interface Dependent {
  id: string;
  /** "spouse" | "child" — drives the label; both ask the same fields. */
  relationship: "spouse" | "child";
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  sex: Sex | "";
  /**
   * Home ZIP. When the employee's "same ZIP" toggle is on this is ignored and
   * the member's ZIP is used at submit; when off it holds the per-dependent ZIP.
   */
  zip: string;
}

/**
 * How an employee's dependent data is collected:
 *  - `manual` → the employer is typing the dependents in themselves.
 *  - `none`   → no dependents (employee-only coverage).
 *  - null     → not decided yet.
 *
 * (The demo's `invite` path was removed — this flow is manual-only.)
 */
export type DependentMethod = "manual" | "none" | null;

/**
 * Why a team member is waiving coverage — asked when they're marked "Not
 * enrolling". The standard set of reasons someone declines employer health
 * benefits; tells the employer/broker whether the waiver is coverage-elsewhere
 * (expected) or a genuine opt-out. Mirrors the employee-link flow's reasons and
 * the `quote_census_waive_reason` DB enum.
 */
export type WaiveReason =
  | "spouse-plan"
  | "parent-plan"
  | "marketplace"
  | "government"
  | "declining"
  | "other";

/** One person on the team — the underwriting basics we ask the employer for. */
export interface TeamMember {
  id: string;
  /**
   * The connected HR system's own id for this person (QuickBooks employee id,
   * BambooHR employee id), when the row came from a pull.
   *
   * Its whole job is to survive a re-import: if the employer goes back and
   * connects again, rows are matched on this and keep every correction the
   * employer typed. Matching on name instead would break precisely when someone
   * fixed a misspelled name — the case most likely to be edited. Null for
   * manually added and CSV-uploaded people.
   */
  externalId?: string | null;
  firstName: string;
  lastName: string;
  sex: Sex | "";
  zip: string;
  dateOfBirth: string;
  /** Set on the dependents step. */
  dependentMethod: DependentMethod;
  dependents: Dependent[];
  /**
   * Whether this member's dependents share the member's home ZIP. Defaults to
   * on (undefined === true); when the employer turns it off, each dependent
   * gets its own ZIP field (pre-filled from the member).
   */
  dependentsSameZip?: boolean;
  /**
   * Whether this person is enrolling in the medical plan. Defaults to enrolled
   * (undefined === true); the dependents panel lets the employer waive anyone
   * covered elsewhere, which drops them from the rate.
   */
  enrolling?: boolean;
  /**
   * Why they're waiving — asked (and required) only when `enrolling === false`.
   * Cleared when the member is re-enrolled.
   */
  notEnrollingReason?: WaiveReason | null;
}
